import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import { T } from "../app/tokens.js";
import { Tooltip } from "./Tooltip.jsx";
import { useHoverIntent } from "./useHoverIntent.js";
import {
  formatReportingPeriodCoverageTooltip,
  formatOverviewAttendanceCoverageTooltip,
  formatHomeAttendanceCoverageTooltip,
  formatReportingMonth,
  isReportingPeriodIncomplete,
} from "../services/reportingPeriod.js";

/**
 * Month/status text with an optional accessible coverage explainer.
 * The tooltip is portalled and fixed-positioned to escape sticky/overflow
 * ancestors without changing the surrounding label's height.
 */
export function ReportingPeriodLabel({
  reportingPeriod,
  children,
  testId = "reporting-period-label",
  infoTestId = "reporting-period-coverage-info",
  copyVariant = "default",
  style,
}) {
  const triggerRef = useRef(null);
  const { open, triggerProps } = useHoverIntent();
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const incomplete = isReportingPeriodIncomplete(reportingPeriod);
  const month = formatReportingMonth(reportingPeriod?.month, { long: true });
  const tooltipText = incomplete
    ? copyVariant === "overview"
      ? formatOverviewAttendanceCoverageTooltip(reportingPeriod)
      : copyVariant === "home"
        ? formatHomeAttendanceCoverageTooltip(reportingPeriod)
      : formatReportingPeriodCoverageTooltip(reportingPeriod)
    : null;
  const tooltipId = `${infoTestId}-tooltip`;

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = Math.min(280, window.innerWidth - 16);
    setCoords({
      top: Math.min(rect.bottom + 6, window.innerHeight - 72),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
    });
  }, [open]);

  return (
    <span
      data-testid={testId}
      style={{ display: "inline-flex", alignItems: "center", minWidth: 0, ...style }}
    >
      {children}
      {incomplete && tooltipText && (
        <>
          <button
            ref={triggerRef}
            type="button"
            data-testid={infoTestId}
            aria-label={`Attendance coverage information for ${month}`}
            aria-describedby={open ? tooltipId : undefined}
            {...triggerProps}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "0 0 14px",
              width: 14,
              height: 14,
              marginLeft: 4,
              padding: 0,
              border: 0,
              borderRadius: "50%",
              color: T.mid,
              background: "transparent",
              cursor: "help",
              lineHeight: 1,
            }}
          >
            <Info size={12} aria-hidden="true" />
          </button>
          {open && createPortal(
            <Tooltip
              id={tooltipId}
              data-testid={`${infoTestId}-tooltip`}
              style={{
                position: "fixed",
                zIndex: 10000,
                top: coords.top,
                left: coords.left,
                width: 280,
                maxWidth: "calc(100vw - 16px)",
                boxSizing: "border-box",
                whiteSpace: "normal",
                pointerEvents: "none",
              }}
            >
              {tooltipText}
            </Tooltip>,
            document.body,
          )}
        </>
      )}
    </span>
  );
}